import { Outlet, useLocation } from 'react-router-dom';
import { usePermissions } from '@helios/auth-firebase-web';
import { accessForPath } from './permissionMap';
import { NoAccess } from '../components/NoAccess';

/**
 * PRD.268 — route-level authorization. Used as a layout route wrapping all
 * protected shells. Resolves the required permission for the current path and
 * renders the destination (`<Outlet />`) or a No-Access screen.
 *
 * Fail closed: an unmapped protected route is denied and a development warning
 * is emitted (so missing metadata is caught, not silently allowed). Permission
 * loading/flash is handled upstream (routes are not rendered until permissions
 * are ready), so no loading state is needed here.
 */
export function RouteGuard() {
  const { pathname } = useLocation();
  const { canAccess } = usePermissions();
  const access = accessForPath(pathname);

  switch (access.kind) {
    case 'public':
    case 'authOnly':
      return <Outlet />;
    case 'permission':
      if (canAccess(access.requirement)) return <Outlet />;
      return (
        <NoAccess
          requirementLabel={`${access.requirement.module} / ${access.requirement.subModule}`}
        />
      );
    case 'unmapped':
    default:
      if (import.meta.env.DEV) {
        console.warn(
          `[RouteGuard] No permission mapping for "${pathname}" — failing closed. ` +
            `Add a rule in auth/permissionMap.ts or mark it public/auth-only.`,
        );
      }
      return <NoAccess detail={`Unmapped route: ${pathname}`} />;
  }
}
